import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Clock, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import Seo, { buildBreadcrumbLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import { articles, plannedTopics } from "../data/articles";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Ratgeber", to: "/ratgeber" },
];

export default function Ratgeber() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("Alle");

  const cats = useMemo(() => ["Alle", ...new Set(articles.map((a) => a.category))], []);

  const filtered = articles.filter((a) => {
    const matchQ =
      a.title.toLowerCase().includes(query.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(query.toLowerCase());
    const matchC = cat === "Alle" || a.category === cat;
    return matchQ && matchC;
  });

  return (
    <div data-testid="page-ratgeber">
      <Seo
        path="/ratgeber"
        title="Ratgeber rund um Entrümpelung & Haushaltsauflösung | Rümpeltaxi"
        description="Der Ratgeber von Rümpeltaxi: verständliche Artikel zu Kosten, Ablauf, besenreiner Übergabe und mehr rund um Entrümpelung und Haushaltsauflösung."
        jsonLd={buildBreadcrumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero
        kicker="Ratgeber"
        title="Wissen rund um Entrümpelung und Auflösung"
        lead="Verständliche, seriöse Artikel zu Kosten, Abläufen und häufigen Fragen. Wir erweitern den Ratgeber laufend."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-page">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mid-gray" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ratgeber durchsuchen …"
                className="w-full rounded-sm border border-light-gray bg-light-gray py-3 pl-10 pr-4 text-base text-brand-black outline-none focus:border-yellow focus:bg-white"
                data-testid="ratgeber-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-sm px-4 py-2 font-heading text-sm font-semibold transition-colors ${
                    cat === c ? "bg-yellow text-brand-black" : "border border-light-gray bg-light-gray text-anthracite hover:border-yellow"
                  }`}
                  data-testid={`ratgeber-cat-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <StaggerGroup className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <motion.div key={a.slug} variants={itemVariants}>
                <Link
                  to={`/ratgeber/${a.slug}`}
                  className="group flex h-full flex-col rounded-sm border border-light-gray bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  data-testid={`article-card-${a.slug}`}
                >
                  <span className="inline-flex w-fit rounded-sm bg-light-gray px-3 py-1 text-xs font-semibold uppercase tracking-widest text-mid-gray">
                    {a.category}
                  </span>
                  <h2 className="mt-4 font-heading text-xl font-bold text-anthracite">{a.title}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-mid-gray">{a.excerpt}</p>
                  <div className="mt-5 flex items-center gap-4 text-xs text-mid-gray">
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.readingTime}</span>
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {a.published}</span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 font-heading text-sm font-bold text-anthracite">
                    Artikel lesen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </StaggerGroup>

          {filtered.length === 0 && (
            <p className="mt-10 text-base text-mid-gray">Keine Artikel gefunden.</p>
          )}

          <Reveal className="mt-16">
            <h2 className="font-heading text-xl font-bold text-anthracite">In Vorbereitung</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {plannedTopics.map((t) => (
                <span key={t} className="rounded-sm border border-dashed border-mid-gray/30 bg-light-gray px-3 py-1.5 text-sm text-mid-gray">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
